import { supabase } from '../lib/supabaseClient';
import type { WorkflowMode, ChatMessage, RollingSummary, PropertyRecord } from '../types/aiChat';

export interface AIChatSession {
  id: string;
  user_id: string;
  organization_id: string | null;
  title: string;
  workflow_mode: WorkflowMode;
  messages: ChatMessage[];
  rolling_summary: RollingSummary | null;
  property_record: PropertyRecord | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
}

/**
 * Create a new AI chat session
 */
export async function createChatSession(
  workflowMode: WorkflowMode,
  title: string,
  rollingSummary: RollingSummary,
  propertyRecord: PropertyRecord
): Promise<AIChatSession | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get user's organization
    const { data: memberData } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', user.id)
      .single();

    const { data, error } = await supabase
      .from('ai_chat_sessions')
      .insert({
        user_id: user.id,
        organization_id: memberData?.organization_id || null,
        title,
        workflow_mode: workflowMode,
        messages: [],
        rolling_summary: rollingSummary,
        property_record: propertyRecord,
        last_message_at: null
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating chat session:', error);
    return null;
  }
}

/**
 * Get all chat sessions for current user
 */
export async function getChatSessions(): Promise<AIChatSession[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('ai_chat_sessions')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching chat sessions:', error);
    return [];
  }
}

/**
 * Get a specific chat session by ID
 */
export async function getChatSession(sessionId: string): Promise<AIChatSession | null> {
  try {
    const { data, error } = await supabase
      .from('ai_chat_sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching chat session:', error);
    return null;
  }
}

/**
 * Update chat session with new messages and context
 */
export async function updateChatSession(
  sessionId: string,
  messages: ChatMessage[],
  rollingSummary: RollingSummary | null,
  propertyRecord: PropertyRecord | null
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('ai_chat_sessions')
      .update({
        messages,
        rolling_summary: rollingSummary,
        property_record: propertyRecord,
        last_message_at: new Date().toISOString()
      })
      .eq('id', sessionId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating chat session:', error);
    return false;
  }
}

/**
 * Rename a chat session
 */
export async function renameChatSession(sessionId: string, newTitle: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('ai_chat_sessions')
      .update({ title: newTitle })
      .eq('id', sessionId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error renaming chat session:', error);
    return false;
  }
}

/**
 * Delete a chat session
 */
export async function deleteChatSession(sessionId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('ai_chat_sessions')
      .delete()
      .eq('id', sessionId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting chat session:', error);
    return false;
  }
}
